import type { CSSProperties, FC } from "react";
import { DefaultInputMax, DefaultInputMin, DefaultInputStep } from "@/components/inputs/shared.ts";
import { styled } from "@linaria/react";
import { theme } from "@/themes/theme.ts";

interface SliderProps {
  id: string;
  min?: number;
  max?: number;
  step?: number;
  state: number;
  onChange: (newNumber: number) => void;
  fillColor?: string;
  trackColor?: string;
  fillFromMiddle?: boolean;
}

const SliderInput = styled.input`
  -webkit-appearance: none;
  appearance: none;

  width: 100%;
  cursor: pointer;

  height: 8px;

  @property --fillColor {
    syntax: "<color>";
    initial-value: red;
    inherits: false;
  }

  @property --trackColor {
    syntax: "<color>";
    initial-value: purple;
    inherits: true;
  }

  @property --pos {
    syntax: "<percentage>";
    initial-value: 0%;
    inherits: false;
  }

  transition:
    --fillColor 500ms linear,
    --trackColor 500ms linear;

  background: linear-gradient(
    to right,
    var(--fillColor) 0%,
    var(--fillColor) var(--pos),
    ${theme.brandSecondary} var(--pos),
    ${theme.brandSecondary} 100%
  );
  border-radius: 16px;

  &[data-middle="true"] {
    background: linear-gradient(
      to right,
      ${theme.brandSecondary} 0%,
      ${theme.brandSecondary} 50%,
      var(--fillColor) 50%,
      var(--fillColor) var(--pos),
      ${theme.brandSecondary} var(--pos),
      ${theme.brandSecondary} 100%
    );

    &[data-pos-under-half="true"] {
      background: linear-gradient(
        to right,
        ${theme.brandSecondary} 0%,
        ${theme.brandSecondary} var(--pos),
        var(--fillColor) var(--pos),
        var(--fillColor) 50%,
        ${theme.brandSecondary} 50%,
        ${theme.brandSecondary} 100%
      );
    }
  }

  // Note: for some reason I can't join the webkit & moz range without exploding
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    border: 2px solid var(--trackColor);
    height: 20px;
    width: 20px;
    border-radius: 9999px;
    background: white;
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }
`;

export const Slider: FC<SliderProps> = ({
  id,
  min,
  max,
  step,
  state,
  fillFromMiddle,
  onChange,
  fillColor,
  trackColor,
}) => {
  const pos =
    ((state - (min ?? DefaultInputMin)) / ((max ?? DefaultInputMax) - (min ?? DefaultInputMin))) *
    100;

  const fill = fillColor ?? theme.sliderTrackedColor;
  const track = trackColor ?? theme.sliderTrackedColor;
  const underHalf = pos < 50;

  return (
    <>
      <SliderInput
        id={id}
        // Note: I had to use style in the end, using style.background would not apply linear-gradient but would static colours, not clear *why*
        style={
          {
            "--trackColor": track,
            "--fillColor": fill,
            "--pos": `${pos}%`,
          } as CSSProperties
        }
        type={"range"}
        value={state}
        placeholder={state.toString()}
        step={step ?? DefaultInputStep}
        max={max ?? DefaultInputMax}
        min={min ?? DefaultInputMin}
        data-middle={fillFromMiddle}
        data-pos-under-half={underHalf}
        onChange={(e) => {
          const newNumber = Number(e.target.value);
          onChange(newNumber);
        }}
      ></SliderInput>
    </>
  );
};
